import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "@/hooks/useTranslation";
import { pricingI18n } from "@/utils/i18n";
import { useAuth } from "@/hooks/useAuth";
import { usePlanSelectionStore, type PlanType, type PlanSelectionSource } from "@/stores/planSelectionStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { LoginHeader } from "@/components/login";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Heart, User, Mail, Phone, Lock, Check, CreditCard } from "lucide-react";
import { getSubscriptionPlans, type PlansApiResponse } from "@/services/api/subscriptions";
import { PUBLIC_ROUTES } from "../../routes";
import { USER_ROUTES } from "@/routes";

type RegisterFormData = {
  user_name: string;
  user_email: string;
  user_password: string;
  user_password_confirmation: string;
  user_phone?: string;
  acceptTerms: boolean;
  plan?: 'free' | 'standard' | 'premium';
};

export default function RegistrationPage() {
  const { t, currentLanguage } = useTranslation();
  const locale = useRouteLocale();

  usePageMeta({
    title: t("auth.register.metaTitle"),
    description: t("auth.register.metaDescription"),
    locale: locale,
    path: PUBLIC_ROUTES.register(locale),
    alternates: { ro: PUBLIC_ROUTES.register("ro"), en: PUBLIC_ROUTES.register("en") },
  });
  const navigate = useNavigate();
  const { register: registerUser, login, loading } = useAuth();
  const { selectedPlan: storedPlan, selectionSource, clearSelectedPlan, setSelectedPlan: persistPlanSelection } = usePlanSelectionStore();
  const [plansData, setPlansData] = useState<PlansApiResponse | null>(null);
  const currentLocale = locale || 'ro';
  const localeCode = currentLanguage as 'ro' | 'en';
  
  const [honeypotWebsite, setHoneypotWebsite] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(storedPlan || 'free');
  const preselectedPaidPlan = Boolean(
    storedPlan &&
    storedPlan !== 'free' &&
    selectionSource &&
    selectionSource !== 'registration'
  );
  const totalSteps = 5;
  const progress = Math.min(100, (currentStep / totalSteps) * 100);

  // Update local state when stored plan changes
  useEffect(() => {
    if (storedPlan) {
      setSelectedPlan(storedPlan);
    }
  }, [storedPlan]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getSubscriptionPlans();
        setPlansData(response);
      } catch (error) {
        console.error('Failed to fetch plans for registration:', error);
      }
    };
    fetchPlans();
  }, []);

  // Create schema with translated messages
  const registerSchema = z.object({
    user_name: z.string().min(2, t('validation.nameMinLength')),
    user_email: z.string().email(t('validation.emailInvalid')),
    user_password: z.string().min(8, t('validation.passwordMinLength')),
    user_password_confirmation: z.string(),
    user_phone: z.string().optional(),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: t('validation.acceptTermsRequired'),
    }),
  }).refine((data) => data.user_password === data.user_password_confirmation, {
    message: t('validation.passwordsDontMatch'),
    path: ["user_password_confirmation"],
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    trigger,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  });

  const watchedAcceptTerms = watch('acceptTerms');

  const selectPlan = useCallback(
    (plan: PlanType, source: PlanSelectionSource = 'registration') => {
      setSelectedPlan(plan);
      persistPlanSelection(plan, source);
    },
    [persistPlanSelection],
  );

  const getPlanCopy = useCallback((plan: PlanType) => {
    const translationBase = `landing.pricing.${plan}`;
    const legacyBase = `pricing.${plan}`;
    const planConfig = plansData?.plans[plan];
    const template =
      t(`${translationBase}.priceTemplate`) || t(`${legacyBase}.priceTemplate`) || '{{price}} {{currency}}';

    // Use API data if available, otherwise fallback to 0 for free plan
    const price = planConfig ? planConfig.price.toString() : (plan === 'free' ? '0' : '—');
    const currency = planConfig?.currency || 'RON';
    const resolvedPrice = template
      .replace('{{price}}', price)
      .replace('{{currency}}', currency);

    return {
      name: t(`${translationBase}.name`) || t(`${legacyBase}.name`) || plan,
      description: t(`${translationBase}.description`) || t(`${legacyBase}.description`) || '',
      price: resolvedPrice,
    };
  }, [plansData, t]);

  const validateStep = async (step: number): Promise<boolean> => {
    switch (step) {
      case 1:
        return await trigger('user_name');
      case 2:
        return await trigger('user_email');
      case 3:
        // Phone is optional, so always allow proceeding
        return true;
      case 4:
        return await trigger(['user_password', 'user_password_confirmation', 'acceptTerms']);
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit(onSubmit)();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(`/${currentLocale}`);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const regResponse = await registerUser(
        data.user_email,
        data.user_password,
        data.user_name,
        data.user_phone,
        { skipRedirect: true, website: honeypotWebsite || undefined }
      );

      const planSource: PlanSelectionSource = selectionSource ?? 'registration';
      persistPlanSelection(selectedPlan, planSource);

      // JUB-220: Real customer accounts require email verification before login.
      if (regResponse?.requiresEmailVerification) {
        sessionStorage.setItem('pending_verification_email', data.user_email);
        navigate(
          `${PUBLIC_ROUTES.emailVerification(currentLocale)}?email=${encodeURIComponent(data.user_email)}`
        );
        return;
      }
      
      // JUB-157: a paid plan is bought for one event, so a new account first
      // creates its event; the dashboard then continues to payment for it.
      const redirectTo =
        selectedPlan === 'free'
          ? USER_ROUTES.events(currentLocale)
          : `${USER_ROUTES.events(currentLocale)}?buyPlan=${selectedPlan}`;

      await login(data.user_email, data.user_password, {
        redirectTo,
      });

      if (selectedPlan === 'free') {
        clearSelectedPlan();
      }
    } catch (error) {
      console.error('Registration failed', error);
    }
  };

  const handleGoToLogin = () => {
    navigate(PUBLIC_ROUTES.login(currentLocale));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <User className="h-12 w-12 text-pink-600 dark:text-pink-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-foreground">
                {t('auth.step1.title')}
              </h3>
              <p className="text-muted-foreground mt-2">
                {t('auth.step1.subtitle')}
              </p>
            </div>
            <div>
              <Label htmlFor="user_name">
                {t('auth.name')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="user_name"
                type="text"
                autoComplete="name"
                placeholder={t('auth.name')}
                {...register('user_name')}
                className="mt-1"
                key="step1-name"
                data-testid="register-name-input"
              />
              {errors.user_name && (
                <p className="mt-1 text-sm text-destructive">{errors.user_name.message}</p>
              )}
            </div>
            {preselectedPaidPlan && (
              <div className="rounded-lg border border-dashed border-pink-200/80 bg-pink-50/50 dark:bg-pink-950/10 p-4">
                <p className="text-sm font-semibold text-foreground">
                  {t('auth.preselectedPlan.title', {
                    plan: t(`pricing.${selectedPlan}.name`) || getPlanCopy(selectedPlan).name,
                  })}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('auth.preselectedPlan.subtitle')}
                </p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Mail className="h-12 w-12 text-pink-600 dark:text-pink-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-foreground">
                {t('auth.step2.title')}
              </h3>
              <p className="text-muted-foreground mt-2">
                {t('auth.step2.subtitle')}
              </p>
            </div>
            <div>
              <Label htmlFor="user_email">
                {t('auth.email')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="user_email"
                type="email"
                autoComplete="email"
                placeholder={t('auth.email')}
                {...register('user_email')}
                className="mt-1"
                key="step2-email"
                value={watch('user_email') || ''}
                data-testid="register-email-input"
              />
              {errors.user_email && (
                <p className="mt-1 text-sm text-destructive">{errors.user_email.message}</p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Phone className="h-12 w-12 text-pink-600 dark:text-pink-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-foreground">
                {t('auth.step3.title')}
              </h3>
              <p className="text-muted-foreground mt-2">
                {t('auth.step3.subtitle')}
              </p>
            </div>
            <div>
              <Label htmlFor="user_phone">
                {t('auth.phone')}
              </Label>
              <Input
                id="user_phone"
                type="tel"
                autoComplete="tel"
                placeholder={t('auth.phone')}
                {...register('user_phone')}
                className="mt-1"
                key="step3-phone"
                value={watch('user_phone') || ''}
              />
              {errors.user_phone && (
                <p className="mt-1 text-sm text-destructive">{errors.user_phone.message}</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Lock className="h-12 w-12 text-pink-600 dark:text-pink-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-foreground">
                {t('auth.step4.title')}
              </h3>
              <p className="text-muted-foreground mt-2">
                {t('auth.step4.subtitle')}
              </p>
            </div>
            <div>
              <Label htmlFor="user_password">
                {t('auth.password')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="user_password"
                type="password"
                autoComplete="new-password"
                placeholder={t('auth.password')}
                {...register('user_password')}
                className="mt-1"
                key="step4-password"
                value={watch('user_password') || ''}
                data-testid="register-password-input"
              />
              {errors.user_password && (
                <p className="mt-1 text-sm text-destructive">{errors.user_password.message}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                {t('validation.passwordHint')}
              </p>
            </div>
            <div>
              <Label htmlFor="user_password_confirmation">
                {t('auth.confirmPassword')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="user_password_confirmation"
                type="password"
                autoComplete="new-password"
                placeholder={t('auth.confirmPassword')}
                {...register('user_password_confirmation')}
                className="mt-1"
                key="step4-password-confirm"
                value={watch('user_password_confirmation') || ''}
                data-testid="register-password-confirmation-input"
              />
              {errors.user_password_confirmation && (
                <p className="mt-1 text-sm text-destructive">{errors.user_password_confirmation.message}</p>
              )}
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acceptTerms"
                checked={watchedAcceptTerms ?? false}
                onCheckedChange={(checked: boolean) => setValue('acceptTerms', checked, { shouldValidate: true })}
                className="mt-1"
                data-testid="register-accept-terms-checkbox"
              />
              <Label htmlFor="acceptTerms" className="text-sm leading-relaxed">
                {t('auth.acceptTerms.text')}{' '}
                <Link
                  to={`/${currentLocale}/terms`}
                  className="text-pink-600 hover:text-pink-500 underline"
                >
                  {t('auth.acceptTerms.link')}
                </Link>
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-destructive">{errors.acceptTerms.message}</p>
            )}
          </div>
        );

      case 5: {
        const freePlan = getPlanCopy('free');
        const standardPlan = getPlanCopy('standard');
        const premiumPlan = getPlanCopy('premium');
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <CreditCard className="h-12 w-12 text-pink-600 dark:text-pink-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-foreground">
                {t('auth.step5.title')}
              </h3>
              <p className="text-muted-foreground mt-2">
                {t('auth.step5.subtitle') || pricingI18n.get('auth.step5.subtitle', localeCode)}
              </p>
            </div>
            {preselectedPaidPlan && (
              <div className="rounded-lg border border-dashed border-pink-200/80 bg-pink-50/50 dark:bg-pink-950/10 p-4">
                <p className="text-sm font-semibold text-foreground">
                  {t('auth.preselectedPlan.title', {
                    plan: getPlanCopy(selectedPlan).name,
                  }) || pricingI18n.get('auth.step5.signingUp', localeCode).replace('{{plan}}', getPlanCopy(selectedPlan).name)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('auth.preselectedPlan.subtitle') || pricingI18n.get('auth.step5.confirmPayment', localeCode)}
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4">
              {/* Free Plan */}
              <button
                type="button"
                onClick={() => selectPlan('free')}
                className={cn(
                  "rounded-lg border-2 p-4 text-left transition-all",
                  selectedPlan === 'free'
                    ? "border-pink-500 bg-pink-50 dark:bg-pink-950/20"
                    : "border-border hover:border-pink-300"
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-foreground">
                      {freePlan.name}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {freePlan.description}
                    </p>
                    <p className="text-lg font-bold text-pink-600 mt-2">
                      {freePlan.price}
                    </p>
                  </div>
                  <div className={cn(
                    "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                    selectedPlan === 'free'
                      ? "border-pink-500 bg-pink-500"
                      : "border-border"
                  )}>
                    {selectedPlan === 'free' && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                </div>
              </button>

              {/* Standard Plan */}
              <button
                type="button"
                onClick={() => selectPlan('standard')}
                className={cn(
                  "rounded-lg border-2 p-4 text-left transition-all relative",
                  selectedPlan === 'standard'
                    ? "border-pink-500 bg-pink-50 dark:bg-pink-950/20"
                    : "border-border hover:border-pink-300"
                )}
              >
                <div className="absolute -top-3 left-4">
                  <span className="rounded-full bg-pink-500 px-2 py-0.5 text-xs font-semibold text-white">
                    {t('pricing.standard.popular') || pricingI18n.get('pricing.standard.popular', localeCode)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div>
                    <h4 className="font-semibold text-foreground">
                      {standardPlan.name}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {standardPlan.description}
                    </p>
                    <p className="text-lg font-bold text-pink-600 mt-2">
                      {standardPlan.price}
                    </p>
                  </div>
                  <div className={cn(
                    "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                    selectedPlan === 'standard'
                      ? "border-pink-500 bg-pink-500"
                      : "border-border"
                  )}>
                    {selectedPlan === 'standard' && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                </div>
              </button>

              {/* Premium Plan */}
              <button
                type="button"
                onClick={() => selectPlan('premium')}
                className={cn(
                  "rounded-lg border-2 p-4 text-left transition-all",
                  selectedPlan === 'premium'
                    ? "border-pink-500 bg-pink-50 dark:bg-pink-950/20"
                    : "border-border hover:border-pink-300"
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-foreground">
                      {premiumPlan.name}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {premiumPlan.description}
                    </p>
                    <p className="text-lg font-bold text-pink-600 mt-2">
                      {premiumPlan.price}
                    </p>
                  </div>
                  <div className={cn(
                    "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                    selectedPlan === 'premium'
                      ? "border-pink-500 bg-pink-500"
                      : "border-border"
                  )}>
                    {selectedPlan === 'premium' && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <LoginHeader currentLocale={currentLocale} action={{ label: t('header.login'), onClick: handleGoToLogin }} />

      {/* Main Content - same layout and background as login */}
      <div className="flex items-start justify-center min-h-[calc(100vh-4rem)] pt-20 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-pink-50/50 to-background dark:from-pink-950/20 dark:to-background">
        <div className="w-full max-w-md space-y-6">
          {/* Tabs for Login/Register - same styling as LoginTabs */}
          <div className="flex border-b border-border bg-white dark:bg-card rounded-t-lg overflow-hidden">
            <button
              type="button"
              onClick={handleGoToLogin}
              className={cn(
                "flex-1 py-3 px-6 text-center font-medium text-sm transition-colors",
                "border-b-2 border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t('auth.signIn')}
            </button>
            <button
              type="button"
              className={cn(
                "flex-1 py-3 px-6 text-center font-medium text-sm transition-colors",
                "border-b-2 border-pink-500 text-pink-600 font-semibold bg-white dark:bg-card"
              )}
            >
              {t('auth.signUp')}
            </button>
          </div>

          {/* Registration Form Card - same card style as login */}
          <div className="bg-card border rounded-lg p-8 shadow-lg">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Heart className="h-6 w-6 text-pink-600 fill-pink-600" />
                <h2 className="text-2xl font-bold text-pink-600">
                  {t('auth.joinJubiel')}
                </h2>
              </div>
              <Progress value={progress} className="h-2 mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('auth.step')} {currentStep} {t('auth.of')} {totalSteps}
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" key={`form-step-${currentStep}`}>
              <input
                type="text"
                name="website"
                value={honeypotWebsite}
                onChange={(e) => setHoneypotWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="sr-only"
              />
              {renderStep()}

              <div className="flex justify-between space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  className="flex-1"
                  data-testid="register-previous-button"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {currentStep === 1 ? t('common.back') : t('common.previous')}
                </Button>

                <Button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 gradient-romantic text-white"
                  disabled={isSubmitting || loading}
                  data-testid={currentStep === totalSteps ? "register-submit-button" : "register-next-button"}
                >
                  {currentStep === totalSteps ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      {t('common.confirm')}
                    </>
                  ) : (
                    <>
                      {t('common.next')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
